import { useMemo } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartTooltip } from "@/components/dashboard/ChartTooltip";
import { CHART, SERIES, fmtDate, fmtInt, fmtUSD, tickStyle } from "@/lib/format";

const ACTOR_COLORS = { user: CHART.gold, project: CHART.sky, unattributed: CHART.danger };
const ACTOR_LABELS = { user: "Users", project: "Project", unattributed: "Unattributed" };

const xTick = (n) => (n <= 14 ? 0 : n <= 31 ? 2 : n <= 62 ? 6 : Math.ceil(n / 12));

/**
 * Daily stacked bars of AI key usage. `metric` = cost | calls, `stackBy` = actor | section.
 * `sections` is the catalogue (key -> label) so colours stay stable across ranges.
 */
export const UsageChart = ({ series = [], metric = "cost", stackBy = "actor", sections = [], height = 300, testId }) => {
  const keys = useMemo(() => {
    if (stackBy === "actor") return ["user", "project", "unattributed"].map((k) => ({ key: k, label: ACTOR_LABELS[k], color: ACTOR_COLORS[k] }));
    const used = new Set();
    series.forEach((d) => Object.keys(d.by_section || {}).forEach((k) => used.add(k)));
    return sections.filter((s) => used.has(s.key)).map((s, i) => ({ key: s.key, label: s.label, color: SERIES[i % SERIES.length] }));
  }, [series, stackBy, sections]);

  const data = useMemo(
    () =>
      series.map((d) => {
        const row = { date: d.date, total: metric === "cost" ? d.cost_usd || 0 : d.calls || 0, avoided: d.avoided || 0 };
        const src = stackBy === "actor" ? d.by_actor || {} : d.by_section || {};
        keys.forEach((k) => {
          const v = src[k.key];
          row[k.key] = v ? (metric === "cost" ? v.cost_usd || 0 : v.calls || 0) : 0;
        });
        return row;
      }),
    [series, keys, metric, stackBy]
  );
  const empty = !data.some((d) => d.total > 0);
  const fmt = metric === "cost" ? fmtUSD : fmtInt;

  return (
    <div className="chart-frame relative" data-testid={testId || "usage-chart"} style={{ height }}>
      {empty && <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground pointer-events-none z-10">No key usage in this range</div>}
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 8, left: -8, bottom: 0 }} barCategoryGap="28%">
          <CartesianGrid stroke={CHART.grid} vertical={false} />
          <XAxis dataKey="date" tick={tickStyle} tickLine={false} axisLine={{ stroke: CHART.axis }} interval={xTick(data.length)} tickFormatter={(v) => fmtDate(v)} minTickGap={16} />
          <YAxis tick={tickStyle} tickLine={false} axisLine={false} width={56} tickFormatter={(v) => (metric === "cost" ? (v >= 1 ? `$${v.toFixed(v >= 10 ? 0 : 1)}` : `$${v.toFixed(2)}`) : v)} allowDecimals={metric === "cost"} />
          <Tooltip
            cursor={{ fill: "hsl(var(--gold) / 0.06)" }}
            content={
              <ChartTooltip
                labelFormatter={(l, p) => `${fmtDate(l)} · ${metric === "cost" ? fmtUSD(p?.[0]?.payload?.total) : `${fmtInt(p?.[0]?.payload?.total)} calls`}${p?.[0]?.payload?.avoided ? ` · ${p[0].payload.avoided} avoided` : ""}`}
                formatter={(v, name) => [fmt(v), keys.find((k) => k.key === name)?.label || name]}
              />
            }
          />
          {keys.map((k, i) => (
            <Bar key={k.key} dataKey={k.key} stackId="u" fill={k.color} radius={i === keys.length - 1 ? [3, 3, 0, 0] : 0} isAnimationActive={false} maxBarSize={38} />
          ))}
          <Legend verticalAlign="top" height={26} iconType="square" iconSize={8} formatter={(v) => <span className="text-xs text-muted-foreground">{keys.find((k) => k.key === v)?.label || v}</span>} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Two-tone split bar: users vs project share of estimated cost. */
export const SplitBar = ({ users = 0, project = 0, testId }) => {
  const tot = (users || 0) + (project || 0);
  const u = tot ? (users / tot) * 100 : 0;
  return (
    <div className="h-1.5 w-full rounded-full bg-muted/40 overflow-hidden flex" data-testid={testId}>
      <span className="block h-full" style={{ width: `${u}%`, background: ACTOR_COLORS.user }} />
      <span className="block h-full" style={{ width: `${tot ? 100 - u : 0}%`, background: ACTOR_COLORS.project }} />
    </div>
  );
};

export { ACTOR_COLORS, ACTOR_LABELS };
